import type { TOC } from '@ember/component/template-only';
import { fn, get } from '@ember/helper';
import { on } from '@ember/modifier';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert, didUpdate } from '@ember/render-modifiers';
import {
  PrismButton,
  PrismCard,
  PrismIcon,
  PrismIconBrand,
  PrismLoadingBar,
  PrismMessage,
  PrismSelectInput,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and, eq, lte, not } from 'ember-truth-helpers';
import GeneralButtonsButtonsPreviousNextSmall from 'lumen/components/general/buttons/buttons-previous-next-small';
import GeneralCalendarsCalendarBase from 'lumen/components/general/calendars/calendar-base';
import GeneralPageHeader from 'lumen/components/general/page/header';
import GeneralPageMain from 'lumen/components/general/page/main';
import HotelDropdownsCompsetSelection from 'lumen/components/hotel/dropdowns/compset-selection';
import HotelRankingCalendarsDaysDayRanking from 'lumen/components/hotel/ranking/calendars/days/day-ranking';
import HotelRankingCalendarsLegendsLegendRanking from 'lumen/components/hotel/ranking/calendars/legends/legend-ranking';
import HotelRankingChartsChartDistribution from 'lumen/components/hotel/ranking/charts/chart-distribution';
import HotelAndMarketDropdownsAccountSelectorWrapper from 'lumen/components/hotel-and-market/dropdowns/account-selector-wrapper';
import type HotelRankingController from 'lumen/controllers/hotel/ranking';
import dayjsFormat from 'lumen/helpers/dayjs-format';
import otaRankingLink from 'lumen/helpers/ota-ranking-link';

interface HotelRankingSignature {
  Args: {
    controller: HotelRankingController;
    model: unknown;
  };
}

<template>
  {{! @glint-nocheck: Need to convert controller to *.ts }}
  {{! template-lint-disable require-presentational-children }}
  <GeneralPageHeader {{didInsert @controller.initMonth}}>
    <div class="content-wrapper">
      <div class="flex gap-400">
        <HotelAndMarketDropdownsAccountSelectorWrapper @hasDivider={{true}} />
        <div class="ota-selections-container ranking-navbar gap-200">
          <PrismSelectInput
            @onChange={{@controller.setSelected}}
            @options={{@controller.rankingOtaOptions}}
            @value={{@controller.selectedRankingOta}}
          />
          <PrismSelectInput
            @onChange={{@controller.setTimespan}}
            @options={{@controller.timespans}}
            @value={{@controller.selectedDuration}}
          />
          {{#if @controller.hasCompsetFilter}}
            <HotelDropdownsCompsetSelection
              @hideCompsetsWithNoData={{true}}
              @selectedOTA={{@controller.selectedRankingOta}}
              @setCompsetId={{@controller.setCompsetId}}
            />
          {{/if}}

          <div class="no-filter-content">
            {{t
              "rates.nights"
              count=(get
                @controller.hotelAndMarketService.hotel.defaultLosRates "0"
              )
            }}
          </div>
        </div>
      </div>
      <div class="absolute top-0 right-800 flex h-full items-center">
        <div class="update-timestamp text-200--regular">
          {{#if @controller.model.loading}}
            <div
              class="update-timestamp-loading-placeholder loading-pulse"
            ></div>
          {{else if
            (and @controller.hasUpdateTimestamp @controller.showUpdateTime)
          }}
            {{t "general.updated"}}
            {{@controller.updateTimestamp}}
          {{/if}}
        </div>
      </div>
    </div>
  </GeneralPageHeader>

  <GeneralPageMain
    {{didUpdate
      @controller.doRefresh
      @controller.hotelAndMarketService.availableCompsets
    }}
  >
    <div class="content-wrapper main-content">
      {{#if @controller.model.loading}}
        <PrismLoadingBar @isFixed={{true}} @positionTop={{120}} />
      {{/if}}
      <div class="ota-cards flex flex-row">
        {{#each-in @controller.otas as |id ota|}}
          <div
            class="card ranking-filter-ota {{if ota.selected 'card--selected'}}"
            data-event-id="hotel-ranking-ota-card"
            data-event-value={{id}}
            role="button"
            {{on "click" (fn @controller.setSelected ota)}}
          >
            <div
              class="card-title
                {{if ota.selected 'selected'}}
                flex items-center justify-center gap-200"
            >
              {{#if ota.ota.isConnected}}
                <PrismIconBrand @name={{ota.id}} />
                {{ota.ota.name}}
              {{else}}
                <PrismIcon @color="inherit" @name="connection" />
                {{ota.ota.name}}
              {{/if}}
            </div>
            {{#if @controller.model.loading}}
              <div class="card-content flex flex-row">
                <div class="card-column flex flex-col">
                  <div class="card-content-subtitle">
                    {{t "ranking.ranking"}}
                    <div class="fake-loading-row short centered"></div>
                  </div>
                </div>
                <div class="card-column right flex flex-col">
                  <div class="card-content-subtitle">
                    {{t "ranking.review_score"}}
                    <div class="fake-loading-row short centered"></div>
                  </div>
                </div>
              </div>
            {{else if (not ota.ota.isConnected)}}
              <div
                class="card-content flex flex-row
                  {{if ota.selected 'selected'}}"
              >
                <div class="card-column flex flex-col">
                  <div class="card-content-subtitle">
                    {{t "ranking.ranking"}}
                  </div>
                  <div class="card-content-subcontent">
                    <div class="card-content-subcontent-item">
                      {{t "general.missing"}}
                    </div>
                  </div>
                </div>
                <div class="card-column right flex flex-col">
                  <div class="card-content-subtitle">
                    {{t "ranking.review_score"}}
                  </div>
                  <div class="card-content-subcontent">
                    <div class="card-content-subcontent-item">
                      {{t "general.missing"}}
                    </div>
                  </div>
                </div>
              </div>
            {{else}}
              <div
                class="card-content flex flex-row
                  {{if ota.selected 'selected'}}"
              >
                {{#each ota.rankings as |ranking|}}
                  {{#if
                    (eq
                      @controller.hotelAndMarketService.hotel.hotelinfo.id
                      ranking.hotelinfo.id
                    )
                  }}
                    <div class="card-column flex flex-col">
                      <div class="card-content-subtitle">
                        {{t "ranking.ranking"}}
                      </div>
                      <div class="card-content-subcontent">
                        <div class="card-content-subcontent-item">
                          {{#if (lte ranking.ranking 0)}}
                            {{t ranking.message}}
                          {{else}}
                            {{ranking.ranking}}
                          {{/if}}
                        </div>
                        {{#if ranking.ranking_diff}}
                          <div class="card-content-subcontent-item secondary">
                            <span class={{ranking.ranking_class}}>
                              {{ranking.ranking_diff}}
                            </span>
                          </div>
                        {{/if}}
                      </div>
                    </div>
                    <div class="card-column right flex flex-col">
                      <div class="card-content-subtitle">
                        {{t "ranking.review_score"}}
                      </div>
                      <div class="card-content-subcontent">
                        <div class="card-content-subcontent-item">
                          {{#if (lte ranking.reviewScore 0)}}
                            {{t "general.missing"}}
                          {{else}}
                            {{ranking.reviewScore}}
                          {{/if}}
                        </div>
                        {{#if ranking.review_diff}}
                          <div class="card-content-subcontent-item secondary">
                            <span class={{ranking.review_class}}>
                              {{ranking.review_diff}}
                            </span>
                          </div>
                        {{/if}}
                      </div>
                    </div>
                  {{/if}}
                {{/each}}
              </div>
            {{/if}}
          </div>
        {{/each-in}}
      </div>
      {{#if (not @controller.selectedOTA.ota.isConnected)}}
        <div
          class="ota-disconnected-empty-state flex flex-row items-center justify-center"
        >
          <PrismMessage
            @description={{t "rates.disconnected_ota"}}
            @illustration="100x100_critical"
            @title={{t "rates.disconnected_ota_title"}}
          />
        </div>
      {{else if @controller.selectedOTA.ota.hasRankingOverview}}
        <div class="flex flex-row">
          <PrismCard @contentLayout="noPadding" class="table-card">
            <:header as |Header|>
              <div class="flex items-center justify-start">
                <Header.title>
                  {{t "settings.competitors.compset"}}
                </Header.title>
                {{#if @controller.currentHotelData}}
                  <span class="available-hotels ml-200">
                    {{@controller.propertiesListed}}&nbsp;{{t
                      "ranking.properties_listed"
                    }}
                  </span>
                {{/if}}
              </div>
            </:header>

            <:content>
              {{#if @controller.model.loading}}
                <table>
                  <thead>
                    <tr class="">
                      <th class="table-card-hotelname">
                        {{t "ranking.hotel_header"}}
                      </th>
                      <th class="table-centered-title">
                        {{t "ranking.ranking"}}
                      </th>
                      <th class="table-centered-title">
                        {{t "ranking.review_score"}}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {{#each @controller.loadingStateRows as |row|}}
                      {{#if row.empty}}
                        <tr>
                          <td class="table-card-hotelname"></td>
                          <td class="table-card-ranking clearfix"></td>
                          <td class="table-card-review clearfix"></td>
                        </tr>
                      {{else}}
                        <tr>
                          <td class="table-card-hotelname">
                            <div class="fake-loading-row long"></div>
                          </td>
                          <td class="table-card-ranking clearfix">
                            <div class="fake-loading-row short centered"></div>
                          </td>
                          <td class="table-card-review clearfix">
                            <div class="fake-loading-row short centered"></div>
                          </td>
                        </tr>
                      {{/if}}
                    {{/each}}
                  </tbody>
                </table>
              {{else}}
                <table>
                  <thead>
                    <tr class="">
                      <th class="table-card-hotelname">
                        {{t "ranking.hotel_header"}}
                      </th>
                      <th class="table-centered-title">
                        {{t "ranking.ranking"}}
                      </th>
                      <th class="table-centered-title">
                        {{t "ranking.review_score"}}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {{#each-in
                      @controller.currentSelectedPeriod
                      as |hotel ranking|
                    }}
                      {{#if
                        (not
                          (and
                            (lte ranking.ranking 0) (lte ranking.reviewScore 0)
                          )
                        )
                      }}
                        <tr>
                          <td
                            class="table-card-hotelname
                              {{if
                                (eq
                                  @controller.hotelAndMarketService.hotel.hotelinfo.id
                                  ranking.hotelinfo.id
                                )
                                'current-hotel'
                              }}"
                          >
                            {{otaRankingLink
                              ranking.ota.id
                              ranking.hotelinfo.id
                              2
                              false
                              ranking.hotelinfo.name
                            }}
                          </td>
                          <td class="table-card-ranking clearfix">
                            {{#if (lte ranking.ranking 0)}}
                              <div class="centered-text">
                                {{t ranking.message}}
                              </div>
                            {{else}}
                              <div class="left-column">
                                {{ranking.ranking}}
                              </div>
                              {{#if ranking.ranking_diff}}
                                <div
                                  class="right-column {{ranking.ranking_class}}"
                                >
                                  {{ranking.ranking_diff}}
                                </div>
                              {{/if}}
                            {{/if}}
                          </td>
                          <td class="table-card-review clearfix">
                            {{#if (lte ranking.reviewScore 0)}}
                              <div class="centered-text">
                                {{t "general.missing"}}
                              </div>
                            {{else}}
                              <div class="left-column">
                                {{ranking.reviewScore}}
                              </div>
                              {{#if ranking.review_diff}}
                                <div
                                  class="right-column {{ranking.review_class}}"
                                >
                                  {{ranking.review_diff}}
                                </div>
                              {{/if}}
                            {{/if}}
                          </td>
                        </tr>
                      {{/if}}
                    {{/each-in}}
                  </tbody>
                </table>
              {{/if}}
            </:content>
          </PrismCard>
        </div>
        <div class="flex flex-col">
          <PrismCard class="table-card">
            <:header as |Header|>
              <div class="flex items-center justify-start">
                <Header.title>
                  {{t "ranking.ranking"}}
                </Header.title>
                {{#if @controller.currentHotelData}}
                  <span class="available-hotels ml-200">
                    {{@controller.propertiesListed}}&nbsp;{{t
                      "ranking.properties_listed"
                    }}
                  </span>
                {{/if}}
              </div>
            </:header>

            <:headerAfter>
              <PrismButton
                @buttonType="ghost"
                @icon="excel-export"
                @onClick={{fn @controller.exportRanking true}}
              />
            </:headerAfter>

            <:content>
              {{#unless @controller.model.loading}}
                <HotelRankingChartsChartDistribution
                  @dataForSelectedPeriod={{@controller.dataForChart}}
                  @period={{@controller.timespan}}
                  @type="ranking"
                />
              {{/unless}}
            </:content>
          </PrismCard>

          <PrismCard class="table-card">
            <:header as |Header|>
              <div class="flex items-center justify-start">
                <Header.title>
                  {{t "ranking.review_score"}}
                </Header.title>
                {{#if @controller.currentHotelData}}
                  <span class="available-hotels ml-200">
                    {{@controller.propertiesListed}}&nbsp;{{t
                      "ranking.properties_listed"
                    }}
                  </span>
                {{/if}}
              </div>
            </:header>

            <:headerAfter>
              <PrismButton
                @buttonType="ghost"
                @icon="excel-export"
                @onClick={{fn @controller.exportRanking false}}
              />
            </:headerAfter>

            <:content>
              {{#unless @controller.model.loading}}
                <HotelRankingChartsChartDistribution
                  @dataForSelectedPeriod={{@controller.dataForChart}}
                  @period={{@controller.timespan}}
                  @type="reviewScore"
                />
              {{/unless}}
            </:content>
          </PrismCard>
        </div>
      {{/if}}
      {{#if
        (and
          @controller.selectedOTA.ota.hasRanking
          @controller.selectedOTA.ota.isConnected
        )
      }}
        <PrismCard @contentLayout="noPadding" class="calendar-card">
          <:header as |Header|>
            <Header.title>
              {{t "ranking.ranking_per_arrival_date"}}
            </Header.title>
          </:header>

          <:headerAfter>
            <PrismButton
              @buttonType="ghost"
              @icon="excel-export"
              @onClick={{@controller.handleExport}}
            />
          </:headerAfter>

          <:content>
            <div class="calendar-card-body flex flex-col">
              <div class="month-picker-div">
                <GeneralButtonsButtonsPreviousNextSmall
                  @beginReached={{@controller.previousMonthDisabled}}
                  @endReached={{@controller.nextMonthDisabled}}
                  @nextClicked={{@controller.nextMonth}}
                  @previousClicked={{@controller.previousMonth}}
                />
                <h4>{{dayjsFormat
                    @controller.monthMoment
                    format=@controller.localisedFormat
                  }}</h4>
              </div>
              <div class="ranking-calendar-div">
                <GeneralCalendarsCalendarBase
                  @dayComponent={{HotelRankingCalendarsDaysDayRanking}}
                  @hasLegend={{true}}
                  @isLoading={{@controller.model.periods.loading}}
                  @model={{@controller.monthOnlyModel}}
                >
                  <:dayComponent as |Component|>
                    <Component.day
                      @arrivalRankingInfo={{@controller.arrivalRankingInfo}}
                      @competitorsForCurrentHotelAndOTA={{@controller.competitorsForCurrentHotelAndOTA}}
                      @monthMoment={{@controller.monthMoment}}
                      @selectedOTA={{@controller.selectedOTA.ota}}
                    />
                  </:dayComponent>

                  <:legend>
                    <HotelRankingCalendarsLegendsLegendRanking />
                  </:legend>
                </GeneralCalendarsCalendarBase>
              </div>
            </div>
          </:content>
        </PrismCard>
      {{/if}}
    </div>
  </GeneralPageMain>
</template> satisfies TOC<HotelRankingSignature>;
